/**
 * Shared chat-row presentation for the sidebar lists.
 *
 * Both the admin history (`sidebar-all-chats`) and the member task peek
 * (`sidebar-recent-tasks`) render the same kind of row, so the icon, the
 * streaming spinner, the typed-title animation and the delete affordance live
 * here rather than being reimplemented per list. A row that looks the same must
 * behave the same — a second copy of this logic drifts the moment one list
 * gains a state the other doesn't.
 *
 * @module layout/sidebar/chat-row-live-state
 */

import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { DynamicIcon } from 'lucide-react/dynamic'
import { useChatEvents } from '@/hooks/use-chat-events'
import { resolveChatIcon } from '@/lib/chat-icon'
import { cn } from '@/lib/utils'

/** Delay between typed characters when animating a streamed title. */
const TITLE_TYPING_DELAY_MS = 30

interface AnimatingTitle {
  title: string
  visibleChars: number
}

/**
 * Unified chat row icon.
 * Renders a spinner while the chat is streaming, otherwise the per-chat
 * AI-generated lucide icon (falls back to a default for old chats without
 * a stored icon).
 */
export function ChatRowIcon({
  isStreaming,
  iconName,
  className,
}: {
  isStreaming: boolean
  iconName: string | null | undefined
  /** Size/color override — the search palette renders it inside a smaller box. */
  className?: string
}) {
  if (isStreaming) {
    return <Loader2 className={cn('size-4 shrink-0 animate-spin text-primary', className)} />
  }
  const resolved = resolveChatIcon(iconName)
  return (
    <DynamicIcon
      name={resolved as Parameters<typeof DynamicIcon>[0]['name']}
      className={cn(
        'size-4 shrink-0 text-muted-foreground transition-opacity duration-200',
        className,
      )}
      aria-hidden
    />
  )
}

/**
 * Live per-chat decorations driven by the chat event bus: which rows are
 * streaming, which titles are mid-typewriter, and which AI icons have arrived
 * since the list was fetched.
 *
 * `onDeleted` lets each list react to a deletion its own way (the admin list
 * also navigates away from a deleted chat); returning the setter for delete
 * state keeps the row's spinner local to the list that owns it.
 */
export function useChatRowLiveState({ onDeleted }: { onDeleted?: (chatId: string) => void } = {}) {
  const chatEvents = useChatEvents()
  const [animatingTitles, setAnimatingTitles] = useState<Record<string, AnimatingTitle>>({})
  const [streamingChatIds, setStreamingChatIds] = useState<Set<string>>(new Set())
  const [deletingChatIds, setDeletingChatIds] = useState<Set<string>>(new Set())
  const [iconOverrides, setIconOverrides] = useState<Record<string, string>>({})

  /** Track per-chat in-flight delete state so the row icon can flip to a spinner. */
  const handleDeletingChange = (chatId: string, isDeleting: boolean) => {
    setDeletingChatIds((prev) => {
      const next = new Set(prev)
      if (isDeleting) next.add(chatId)
      else next.delete(chatId)
      return next
    })
  }

  useEffect(() => {
    if (!chatEvents) return

    const unsubscribeDeleteChat = chatEvents.subscribe('delete-chat', (deletedChatId) => {
      onDeleted?.(deletedChatId)
    })

    const unsubscribeUpdateTitle = chatEvents.subscribe('update-title', (chatId, data) => {
      const title = data?.title
      if (!title) return
      setAnimatingTitles((prev) => ({
        ...prev,
        [chatId]: { title, visibleChars: 0 },
      }))
    })

    const unsubscribeUpdateIcon = chatEvents.subscribe('update-icon', (chatId, data) => {
      const icon = data?.icon
      if (!icon) return
      setIconOverrides((prev) => ({ ...prev, [chatId]: icon }))
    })

    const unsubscribeStreamStart = chatEvents.subscribe('stream-start', (chatId) => {
      setStreamingChatIds((prev) => {
        const next = new Set(prev)
        next.add(chatId)
        return next
      })
    })

    const unsubscribeStreamEnd = chatEvents.subscribe('update-chat', (chatId) => {
      setStreamingChatIds((prev) => {
        if (!prev.has(chatId)) return prev
        const next = new Set(prev)
        next.delete(chatId)
        return next
      })
    })

    return () => {
      unsubscribeDeleteChat?.()
      unsubscribeUpdateTitle?.()
      unsubscribeUpdateIcon?.()
      unsubscribeStreamStart?.()
      unsubscribeStreamEnd?.()
    }
  }, [chatEvents, onDeleted])

  useEffect(() => {
    const animatingIds = Object.keys(animatingTitles)
    if (animatingIds.length === 0) return

    const timerId = setTimeout(() => {
      setAnimatingTitles((prev) => {
        const next = { ...prev }
        for (const chatId of animatingIds) {
          const current = next[chatId]
          if (!current) continue
          if (current.visibleChars < current.title.length) {
            next[chatId] = { ...current, visibleChars: current.visibleChars + 1 }
          } else {
            delete next[chatId]
          }
        }
        return next
      })
    }, TITLE_TYPING_DELAY_MS)

    return () => clearTimeout(timerId)
  }, [animatingTitles])

  return {
    animatingTitles,
    streamingChatIds,
    deletingChatIds,
    iconOverrides,
    handleDeletingChange,
  }
}

/**
 * A chat title mid-typewriter, or the settled title. Kept here so both lists
 * animate identically rather than one rendering the final string outright.
 */
export function ChatRowTitle({
  chatId,
  title,
  animatingTitles,
}: {
  chatId: string
  title: string | null | undefined
  animatingTitles: Record<string, AnimatingTitle>
}) {
  const animating = animatingTitles[chatId]
  if (animating) {
    return (
      <>
        {animating.title.slice(0, animating.visibleChars)}
        <span className="animate-pulse">|</span>
      </>
    )
  }
  return <>{title || 'New chat'}</>
}
